import type {
    SketchRegionPlane,
    SketchRegionPolyline,
    SketchRegionVec3,
} from './SketchRegionTypes';
import { normalizeLoopPoints3, pointsAlmostEqual3 } from './SketchRegionMath';

/** 曲线默认采样段数。 */
const CURVE_SEGMENTS = 96;

/**
 * 读取三维元组。
 * @param value 原始值。
 * @returns 三维元组。
 */
function asVec3(value: unknown): SketchRegionVec3 | null {
    if (!Array.isArray(value) || value.length < 3) {
        return null;
    }
    const [x, y, z] = value.map(Number);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        return null;
    }
    return [x, y, z];
}

/**
 * 读取三维点数组。
 * @param value 原始值。
 * @returns 三维点数组。
 */
function asVec3Array(value: unknown): SketchRegionVec3[] | null {
    if (!Array.isArray(value)) {
        return null;
    }
    const points = value
        .map(asVec3)
        .filter((point): point is SketchRegionVec3 => point !== null);
    return points.length >= 2 ? points : null;
}

/**
 * 读取数字字段。
 * @param value 原始值。
 * @returns 数字。
 */
function asNumber(value: unknown): number | null {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
}

/**
 * 按平面轴系合成世界点。
 * @param origin 原点。
 * @param xAxis X 轴。
 * @param yAxis Y 轴。
 * @param x X 坐标。
 * @param y Y 坐标。
 * @returns 世界点。
 */
function combineAxes(
    origin: SketchRegionVec3,
    xAxis: SketchRegionVec3,
    yAxis: SketchRegionVec3,
    x: number,
    y: number,
): SketchRegionVec3 {
    return [
        origin[0] + xAxis[0] * x + yAxis[0] * y,
        origin[1] + xAxis[1] * x + yAxis[1] * y,
        origin[2] + xAxis[2] * x + yAxis[2] * y,
    ];
}

/**
 * 采样圆。
 * @param geometry 几何数据。
 * @param plane 草图平面。
 * @returns 采样点。
 */
function sampleCircle(
    geometry: Record<string, unknown>,
    plane: SketchRegionPlane,
): SketchRegionVec3[] | null {
    const center = asVec3(geometry.center ?? geometry.centerWorld);
    const radius = asNumber(geometry.radius);
    if (!center || !radius || radius <= 0) {
        return null;
    }
    const xAxis = asVec3(geometry.xAxis) ?? plane.xAxis;
    const yAxis = asVec3(geometry.yAxis) ?? plane.yAxis;
    return Array.from({ length: CURVE_SEGMENTS }, (_, index) => {
        const angle = (index / CURVE_SEGMENTS) * Math.PI * 2;
        return combineAxes(center, xAxis, yAxis, Math.cos(angle) * radius, Math.sin(angle) * radius);
    });
}

/**
 * 采样椭圆。
 * @param geometry 几何数据。
 * @param plane 草图平面。
 * @returns 采样点。
 */
function sampleEllipse(
    geometry: Record<string, unknown>,
    plane: SketchRegionPlane,
): SketchRegionVec3[] | null {
    const center = asVec3(geometry.center ?? geometry.centerWorld);
    const radiusX = asNumber(geometry.radiusX);
    const radiusY = asNumber(geometry.radiusY);
    if (!center || !radiusX || !radiusY || radiusX <= 0 || radiusY <= 0) {
        return null;
    }
    const xAxis = asVec3(geometry.xAxis) ?? plane.xAxis;
    const yAxis = asVec3(geometry.yAxis) ?? plane.yAxis;
    const startAngle = asNumber(geometry.startAngle) ?? 0;
    const endAngle = asNumber(geometry.endAngle) ?? Math.PI * 2;
    const rotation = asNumber(geometry.rotation) ?? 0;
    const clockwise = geometry.clockwise === true;
    const rawSweep = clockwise ? startAngle - endAngle : endAngle - startAngle;
    const sweep = Math.abs(rawSweep) <= 1e-9 ? Math.PI * 2 : Math.abs(rawSweep);
    const sampleCount = Math.max(8, Math.ceil(CURVE_SEGMENTS * Math.min(sweep, Math.PI * 2) / (Math.PI * 2)));
    return Array.from({ length: sampleCount + 1 }, (_, index) => {
        const ratio = index / sampleCount;
        const angle = clockwise ? startAngle - sweep * ratio : startAngle + sweep * ratio;
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);
        const cosRotation = Math.cos(rotation);
        const sinRotation = Math.sin(rotation);
        const x = radiusX * cosAngle * cosRotation - radiusY * sinAngle * sinRotation;
        const y = radiusX * cosAngle * sinRotation + radiusY * sinAngle * cosRotation;
        return combineAxes(center, xAxis, yAxis, x, y);
    });
}

/**
 * 采样圆弧。
 * @param geometry 几何数据。
 * @param plane 草图平面。
 * @returns 采样点。
 */
function sampleArc(
    geometry: Record<string, unknown>,
    plane: SketchRegionPlane,
): SketchRegionVec3[] | null {
    const center = asVec3(geometry.center ?? geometry.centerWorld);
    const radius = asNumber(geometry.radius);
    const startAngle = asNumber(geometry.startAngle);
    const endAngle = asNumber(geometry.endAngle);
    if (!center || !radius || radius <= 0 || startAngle === null || endAngle === null) {
        return null;
    }
    return sampleEllipse({
        center,
        radiusX: radius,
        radiusY: radius,
        startAngle,
        endAngle,
        clockwise: geometry.clockwise === true,
        xAxis: geometry.xAxis,
        yAxis: geometry.yAxis,
    }, plane);
}

/**
 * 判断采样点是否闭合。
 * @param points 采样点。
 * @param explicitClosed 显式闭合标记。
 * @returns 是否闭合。
 */
function resolveClosedState(points: SketchRegionVec3[], explicitClosed: unknown): boolean {
    if (explicitClosed === true) {
        return true;
    }
    if (points.length < 3) {
        return false;
    }
    return pointsAlmostEqual3(points[0]!, points[points.length - 1]!);
}

/**
 * 将运行时草图实体转换为折线采样。
 * @param entity 草图实体。
 * @param plane 草图平面。
 * @returns 折线采样。
 */
export function sampleSketchRegionEntity(
    entity: SketchRegionEntityPayload,
    plane: SketchRegionPlane,
): SketchRegionPolyline | null {
    const geometry = entity.geometry ?? {};
    const explicitPoints = asVec3Array(geometry.points)
        ?? asVec3Array(geometry.curvePoints)
        ?? asVec3Array(geometry.controlPoints);
    const sampledPoints = explicitPoints
        ?? (entity.entityType === 'circle' ? sampleCircle(geometry, plane) : null)
        ?? (entity.entityType === 'ellipse' ? sampleEllipse(geometry, plane) : null)
        ?? (entity.entityType === 'arc' ? sampleArc(geometry, plane) : null)
        ?? asVec3Array([geometry.startPoint ?? geometry.start, geometry.endPoint ?? geometry.end]);

    if (!sampledPoints || sampledPoints.length < 2) {
        return null;
    }

    const closed = resolveClosedState(sampledPoints, geometry.isClosed);
    return {
        entityId: entity.entityId,
        entityType: entity.entityType,
        points: closed ? normalizeLoopPoints3(sampledPoints) : sampledPoints,
        closed,
    };
}
